/* eslint-disable react-refresh/only-export-components -- standalone t() and getLang() are intentionally exported alongside the provider */
import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import es from './es.json';
import en from './en.json';
import { getSharedPref, setSharedPref } from '../utils/sharedPrefs';

type Lang = 'es' | 'en';

// Stored in a `.condorsecurity.co` cookie so landing and app subdomains share
// the same choice — localStorage alone is not enough because each origin has
// its own. See utils/sharedPrefs.ts for the fallback strategy.
const STORAGE_KEY = 'condor_lang';
const translations: Record<Lang, typeof es> = { es, en };

// Module-level state — accessible outside React (e.g., api.ts)
let currentLang: Lang = (getSharedPref(STORAGE_KEY) as Lang | null) || 'es';
let current: typeof es = translations[currentLang] || es;

/**
 * Standalone translation function — works outside React components.
 * Resolves dot-path keys like 'errors.session_closed'.
 * Supports interpolation: t('errors.server_error', { status: 500 })
 */
export function t(key: string, params?: Record<string, string | number>): string {
  const keys = key.split('.');
  let value: unknown = current;
  for (const k of keys) {
    if (value == null || typeof value !== 'object') return key;
    value = (value as Record<string, unknown>)[k];
  }
  if (typeof value !== 'string') return key;
  if (!params) return value;
  return value.replace(/\{(\w+)\}/g, (_, name) => String(params[name] ?? `{${name}}`));
}

/** Get current language code */
export function getLang(): Lang {
  return currentLang;
}

// ── React Context ──

interface I18nContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: typeof t;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export function useTranslation() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Fallback: return standalone t() if used outside provider (e.g., ErrorBoundary)
    return { t, lang: currentLang, setLang: () => {} };
  }
  return ctx;
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(currentLang);

  const setLang = useCallback((newLang: Lang) => {
    currentLang = newLang;
    current = translations[newLang] || es;
    setSharedPref(STORAGE_KEY, newLang);
    document.documentElement.lang = newLang;
    setLangState(newLang);
  }, []);

  // Bind t to current render so React re-renders on language change
  const boundT = useCallback((key: string, params?: Record<string, string | number>) => {
    return t(key, params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  return (
    <I18nContext.Provider value={{ lang, setLang, t: boundT }}>
      {children}
    </I18nContext.Provider>
  );
}
